import { Component, Inject, OnInit, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TransportModeService } from 'app/core/mips/common/services/transport-modes.service';
import { SustainabilityEntryService } from 'app/core/mips/common/services/sustainability-entry.service';
import { SnackbarService } from 'app/core/mips/common/services/snackbar.service';
import { Store } from '@ngrx/store';
import { LocalStorageService } from 'app/core/mips/common/services/local-storage.service';

@Component({
  selector: 'app-sustainability-entry-dialog',
  templateUrl: './sustainability-entry-dialog.component.html',
  styleUrls: ['./sustainability-entry-dialog.component.scss'],
})
export class SustainabilityEntryDialogComponent implements OnInit, AfterViewInit {
  unitType: 'Kilometers' | 'Miles' = 'Kilometers';
  transportModes: any[] = [];
  selectedTransportModes: any[] = [];
  currentUserName: string;
  backendKeys: Set<string> = new Set();
  step = 0;
  currentTransportIndex = 0;
  transportDetails: any = [];
  currentYear: number;
  currentMonth: number;

  transportSelectionForm: FormGroup;
  transportDetailForm: FormGroup;
  months = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 }
  ];
  availableMonths: any[] = [];
  years: number[] = [];

  constructor(
    private fb: FormBuilder,
    private travelEntryService: SustainabilityEntryService,
    private snackBarService: SnackbarService,
    private dialogRef: MatDialogRef<SustainabilityEntryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private transportModeService: TransportModeService
  ) {

    const userDataStr = LocalStorageService.getLocalStorageValueBasedOnKeys('currentUser');

    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        this.currentUserName = userData?.currentUser?.LoginName || null;
      } catch (e) {
        console.error('Failed to parse current user data from localStorage', e);
      }
    }
    const today = new Date();
    this.currentYear = today.getFullYear();
    this.currentMonth = today.getMonth() + 1;
    if (this.data) {
      if (this.data.year) {
        this.currentYear = Number(this.data.year) || this.currentYear;
      }
      if (this.data.month != null) {
        if (typeof this.data.month === 'number') {
          this.currentMonth = Number(this.data.month) || this.currentMonth;
        } else if (typeof this.data.month === 'string') {
          const asNum = Number(this.data.month);
          if (!isNaN(asNum)) {
            this.currentMonth = asNum;
          } else {
            const monthNames = [
              'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
            ];
            const idx = monthNames.findIndex(m => m.toLowerCase() === this.data.month.toLowerCase());
            if (idx !== -1) this.currentMonth = idx + 1;
          }
        }
      }
    }

    this.computeAvailableMonths();
    this.updateAvailableYears(this.currentMonth);
    const defaultYear = this.currentYear;
    const defaultMonth = this.currentMonth;

    this.transportSelectionForm = this.fb.group({
      month: [defaultMonth, Validators.required],
      year: [defaultYear, Validators.required],
      transportModes: [[], Validators.required],
    });

    this.transportDetailForm = this.fb.group({
      distance: [null, [Validators.required, Validators.min(0.1), Validators.max(this.maxDistance)]],
      commutingDays: [
        null,
        [Validators.required, Validators.min(1), Validators.max(5), Validators.pattern('^[0-9]+$')]
      ],
    });
  }

  isPreviousMonthRestricted(selectedMonth: number, selectedYear: number): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(Number(selectedYear), Number(selectedMonth), 15);
    deadline.setHours(0, 0, 0, 0);

    return today > deadline;
  }

  isFutureMonthSelected(selectedMonth: number, selectedYear: number): boolean {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    const selectedIndex = Number(selectedYear) * 12 + Number(selectedMonth);
    const currentIndex = currentYear * 12 + currentMonth;
    return selectedIndex > currentIndex;
  }

  isNextMonthSelected(selectedMonth: number, selectedYear: number): boolean {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    let nextMonth = currentMonth + 1;
    let nextYear = currentYear;
    if (nextMonth === 13) { nextMonth = 1; nextYear += 1; }
    return Number(selectedMonth) === nextMonth && Number(selectedYear) === nextYear;
  }

  getFutureMonthMessage(selectedMonth: number, selectedYear: number): string {
    return this.isNextMonthSelected(selectedMonth, selectedYear)
      ? 'Entries cannot be made for next month.'
      : 'Entries cannot be made for future months.';
  }

  isPastMonthSelected(selectedMonth: number, selectedYear: number): boolean {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    const selectedIndex = Number(selectedYear) * 12 + Number(selectedMonth);
    const currentIndex = currentYear * 12 + currentMonth;
    // Past month = any month before current that is NOT the immediate previous month
    // (immediate previous month is handled by isPreviousMonthRestricted)
    let prevMonth = currentMonth - 1;
    let prevYear = currentYear;
    if (prevMonth === 0) { prevMonth = 12; prevYear -= 1; }
    const prevIndex = prevYear * 12 + prevMonth;
    return selectedIndex < currentIndex && selectedIndex !== prevIndex;
  }

  getPastMonthMessage(selectedMonth: number, selectedYear: number): string {
    return 'Entries cannot be made for past months.';
  }

  isSelectionDisabled(): boolean {
    const selectedYear = this.transportSelectionForm?.value?.year;
    const selectedMonth = this.transportSelectionForm?.value?.month;
    return this.isPreviousMonthRestricted(selectedMonth, selectedYear) || this.isFutureMonthSelected(selectedMonth, selectedYear) || this.isPastMonthSelected(selectedMonth, selectedYear);
  }

  ngOnInit(): void {
    this.loadTransportModes();

    // Load backend keys for the initially selected month/year
    const initialYear = this.transportSelectionForm.value.year || this.currentYear;
    const initialMonth = this.transportSelectionForm.value.month || this.currentMonth;
    this.loadBackendKeysForSelection(initialYear, initialMonth);
  }

  ngAfterViewInit(): void {
    if (this.transportSelectionForm) {

      this.transportSelectionForm.get('month')?.valueChanges.subscribe((m) => {
        this.updateAvailableYears(m);

        const y = this.transportSelectionForm.get('year')?.value;

        if (this.isFutureMonthSelected(m, y)) {
          this.snackBarService.showSnackBar(
            this.getFutureMonthMessage(m, y),
            null,
            'failure'
          );

          // Revert to valid values
          this.transportSelectionForm.patchValue(
            {
              month: this.currentMonth,
              year: this.currentYear
            },
            { emitEvent: false }
          );

          return;
        }

        if (this.isPreviousMonthRestricted(m, y)) {
          this.snackBarService.showSnackBar(
            'You cannot add entries for the previous month after the 15th.',
            null,
            'failure'
          );

          // Revert to valid values
          this.transportSelectionForm.patchValue(
            {
              month: this.currentMonth,
              year: this.currentYear
            },
            { emitEvent: false }
          );

          return; 
        }

        if (this.isPastMonthSelected(m, y)) {
          this.snackBarService.showSnackBar(
            this.getPastMonthMessage(m, y),
            null,
            'failure'
          );

          // Revert to valid values
          this.transportSelectionForm.patchValue(
            {
              month: this.currentMonth,
              year: this.currentYear
            },
            { emitEvent: false }
          );

          return;
        }

        if (y && m) {
          this.loadBackendKeysForSelection(y, m);
          }
        });

      this.transportSelectionForm.get('year')?.valueChanges.subscribe((y) => {
        const m = this.transportSelectionForm.get('month')?.value;

        if (this.isFutureMonthSelected(m, y)) {
          this.snackBarService.showSnackBar(
            this.getFutureMonthMessage(m, y),
            null,
            'failure'
          );

          // Revert to valid values
          this.transportSelectionForm.patchValue(
            {
              month: this.currentMonth,
              year: this.currentYear
            },
            { emitEvent: false }
          );

          return;
        }

        if (this.isPreviousMonthRestricted(m, y)) {
          this.snackBarService.showSnackBar(
            'You cannot add entries for the previous month after the 15th.',
            null,
            'failure'
          );
        
          // Revert to valid values
          this.transportSelectionForm.patchValue(
            {
              month: this.currentMonth,
              year: this.currentYear
            },
            { emitEvent: false }
          );

          return;  
        }

        if (this.isPastMonthSelected(m, y)) {
          this.snackBarService.showSnackBar(
            this.getPastMonthMessage(m, y),
            null,
            'failure'
          );

          // Revert to valid values
          this.transportSelectionForm.patchValue(
            {
              month: this.currentMonth,
              year: this.currentYear
            },
            { emitEvent: false }
          );

          return;
        }

        if (y && m) this.loadBackendKeysForSelection(y, m);
      });
    }
  }

  loadBackendKeysForSelection(year: number, month: number) {
    if (!this.currentUserName) return;
    const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
    this.travelEntryService.getSustainabilityData([yearMonth], this.currentUserName)
      .subscribe({
        next: (entries) => {
          this.backendKeys = new Set(
            entries
              .filter(e => !e.travel_type_desc || e.travel_type_desc === 'Daily')
              .map(e => `${(e.transportmode_desc || e.mode || '').toLowerCase()}-${e.month}-${e.year}-${(e.unit || '').toLowerCase()}`)
          );
        },
        error: () => {
          this.snackBarService.showSnackBar('Error loading existing data for selected month.', null, 'failure');
        }
      });
  }



  loadTransportModes(): void {
    const isEditingExistingEntry = this.data && this.data.mode;
    const isModifiedEntry = this.data && this.data.isModifiedEntry;

    // if (isModifiedEntry) {
    //   this.step = 1;
    // } else if (isEditingExistingEntry) {
    //   this.step = 1;
    // }
    this.step = this.data?.startStep ?? 0;

    this.transportModeService.getTransportModes().subscribe(
      (modes) => {
        this.transportModes = modes
          .filter(mode => !['Plane', 'Others'].includes(mode.transportmode_desc))
          .map(mode => ({
            name: mode.transportmode_desc,
            id: mode.transportmode_id
          }));

        if (isEditingExistingEntry) {

          this.unitType = this.data.unit || 'Kilometers';

          const matchedMode = this.transportModes.find(m => m.name === this.data.mode);

          if (matchedMode) {

            this.selectedTransportModes = [matchedMode];
            this.transportSelectionForm.patchValue({
              transportModes: [matchedMode]
            });

          }

          this.transportDetailForm.patchValue({
            distance: this.data.distance,
            commutingDays: this.data.commutingDays,
          });


          if (this.data.year || this.data.month) {
            const patch: any = {};
            if (this.data.year) patch.year = Number(this.data.year);
            if (this.data.month != null) patch.month = typeof this.data.month === 'number' ? Number(this.data.month) : Number(this.data.month) || this.currentMonth;
            this.transportSelectionForm.patchValue(patch);
          }

          if (matchedMode) {
            const curY = this.transportSelectionForm.value.year || this.currentYear;
            const curM = this.transportSelectionForm.value.month || this.currentMonth;
            this.transportDetails = [{
              mode: matchedMode.name,
              transport_mode_id: matchedMode.id,
              distance: this.data.distance,
              commutingDays: this.data.commutingDays,
              year: Number(curY),
              month: Number(curM)
            }];
          }

          this.currentTransportIndex = 0;
          this.configureDistanceControl();
        }
      },
      (error) => {
        console.error('Error fetching transport modes:', error);
      }
    );
  }


  toggleUnit(unit: 'Kilometers' | 'Miles') {
    this.unitType = unit;
    this.configureDistanceControl();
  }
  isTransportFocused = false;

  onTransportFocus(focus: boolean) {
    this.isTransportFocused = focus;
  }


  onNextFromSelection() {
    this.selectedTransportModes = this.transportSelectionForm.value.transportModes || [];
    this.step = 1;
    this.currentTransportIndex = 0;

    const { year, month } = this.transportSelectionForm.value;
    const oldDetails = Array.isArray(this.transportDetails) ? this.transportDetails : [];

    this.transportDetails = this.selectedTransportModes.map(mode => {
      const existing = oldDetails.find(
        (d: any) => d && (d.transport_mode_id === mode.id || (d.mode && mode.name && d.mode.toLowerCase() === mode.name.toLowerCase()))
      );
      if (existing) {
        return {
          ...existing,
          mode: mode.name,
          transport_mode_id: mode.id,
          year: Number(year),
          month: Number(month)
        };
      }
      return null;
    });

    this.loadTransportDetailForCurrentIndex();
    this.configureDistanceControl();
    this.loadBackendKeysForSelection(year, month);
  }

  loadTransportDetailForCurrentIndex(): void {
    const detail = this.transportDetails[this.currentTransportIndex];
    if (detail) {
      this.transportDetailForm.patchValue({
        distance: detail.distance,
        commutingDays: detail.commutingDays
      });
      this.transportDetailForm.markAsPristine();
    } else {
      this.transportDetailForm.reset();
      if (this.isWorkFromHomeSelected()) {
        this.transportDetailForm.patchValue({ distance: 0 });
      }
    }
  }

  onNextTransportDetails() {
    this.configureDistanceControl();
    const currentMode = this.selectedTransportModes[this.currentTransportIndex];
    if (!currentMode) return;

    const detail = {
      mode: currentMode.name,
      transport_mode_id: currentMode.id,
      distance: this.isWorkFromHomeSelected() ? 0 : this.transportDetailForm.value.distance,
      commutingDays: this.transportDetailForm.value.commutingDays,
    };

    const { year, month } = this.transportSelectionForm.value;
    this.transportDetails[this.currentTransportIndex] = {
      ...detail,
      year: Number(year),
      month: Number(month)
    };

    if (this.currentTransportIndex < this.selectedTransportModes.length - 1) {
      this.currentTransportIndex++;
      this.loadTransportDetailForCurrentIndex();
      this.configureDistanceControl();
    } else {
      this.onApply();
    }
  }

  onBack(): void {
    if (this.transportDetailForm.valid) {
      const currentMode = this.selectedTransportModes[this.currentTransportIndex];
      if (currentMode) {
        const { year, month } = this.transportSelectionForm.value;
        this.transportDetails[this.currentTransportIndex] = {
          mode: currentMode.name,
          transport_mode_id: currentMode.id,
          distance: this.isWorkFromHomeSelected() ? 0 : this.transportDetailForm.value.distance,
          commutingDays: this.transportDetailForm.value.commutingDays,
          year: Number(year),
          month: Number(month)
        };
      }
    }

    if (this.currentTransportIndex > 0) {
      this.currentTransportIndex--;
      this.loadTransportDetailForCurrentIndex();
      this.configureDistanceControl();
    } else {
      this.step = 0;
    }
  }

  onApply() {
    const validDetails = (this.transportDetails || []).filter((d: any) => !!d);
    const result = {
      unit: this.unitType,
      transportDetails: validDetails,
    };

    if (!result.transportDetails || result.transportDetails.length === 0) {
      return;
    }

    const selectedYear = this.transportSelectionForm.value.year || this.currentYear;
    const selectedMonth = this.transportSelectionForm.value.month || this.currentMonth;
    const entriesMap = new Map<string, string>();
    const duplicates: string[] = [];

    for (const entry of result.transportDetails) {
      if (entry.isBannerRow) continue;
      const key = `${entry.mode.toLowerCase()}-${entry.month}-${entry.year}-${(this.unitType || '').toLowerCase()}`;

      if (entriesMap.has(key)) {
        duplicates.push(`${entry.mode} for ${this.getMonthName(entry.month)} ${entry.year}`);
      } else {
        entriesMap.set(key, entry.mode);
      }
    }

    if (duplicates.length > 0) {
      this.snackBarService.showSnackBar(
        `Duplicate entries found: ${[...new Set(duplicates)].join(', ')}`,
        null,
        'failure'
      );
      return;
    }

    // Check for duplicates against backend data (already saved entries)
    // Only check when CREATING new entries (not editing existing or dragged entries)
    const isEditMode = this.data && (this.data.isEditMode || this.data.travel_id || this.data.isModifiedEntry || this.data.mode);
    if (!isEditMode) {
      // First, check against backend entries (ADLS)
      const backendDuplicate = result.transportDetails.find(detail => {
        if (detail.isBannerRow) return false;

        const key = `${detail.mode.toLowerCase()}-${selectedMonth}-${selectedYear}-${(this.unitType || '').toLowerCase()}`;
        return this.backendKeys.has(key);
      });

      if (backendDuplicate) {
        this.snackBarService.showSnackBar(
          `Duplicate entry not allowed. ${backendDuplicate.mode} already exists for the selected month.`,
          null,
          'failure'
        );
        return;
      }

      // Second, check against existing unsaved entries in the grid (rowData)
      const existingRowData = this.data?.existingRowData || [];

      const unsavedDuplicate = result.transportDetails.find(detail => {
        if (detail.isBannerRow) return false;

        // Check if this mode already exists in unsaved rowData for the same month/year
        return existingRowData.some(row => {
          if (row.isBannerRow || row.isBlank) return false;

          const rowMonth = Number(row.month);
          const rowYear = Number(row.year);
          const detailMonth = Number(detail.month);
          const detailYear = Number(detail.year);

          const isSameMonthYear = rowMonth === detailMonth && rowYear === detailYear;
          const isSameMode = row.mode?.toLowerCase() === detail.mode?.toLowerCase();
          const isSameUnit = (row.unit || '').toLowerCase() === (this.unitType || '').toLowerCase();

          return isSameMonthYear && isSameMode && isSameUnit;
        });
      });

      if (unsavedDuplicate) {
        this.snackBarService.showSnackBar(
          `Duplicate entry not allowed. ${unsavedDuplicate.mode} already exists in unsaved entries for the selected month.`,
          null,
          'failure'
        );
        return;
      }
    }

    this.dialogRef.close(result);
  }

  getMonthName(monthNumber: number): string {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[monthNumber - 1] || '';
  }


  onCancel() {
    this.dialogRef.close();
  }


  computeAvailableMonths(): void {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    if (this.data?.selectedMonths?.length) {
      this.availableMonths = this.data.selectedMonths.map(m => {
        const found = this.months.find(x => x.value === Number(m));
        return found;
      }).filter(x => x);
    } else {
      // fallback (keep your old logic if needed)
      const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;

      this.availableMonths = this.months.filter(
        m => m.value === currentMonth || m.value === previousMonth
      );
    }

    const activeYear = Number(this.transportSelectionForm?.value?.year || this.currentYear);
    if (activeYear === currentYear) {
      this.availableMonths = this.availableMonths.filter(m => m.value <= currentMonth);
    }

    if (this.currentMonth && !this.availableMonths.some(m => m.value === this.currentMonth)) {
      const found = this.months.find(x => x.value === this.currentMonth);
      if (found) {
        this.availableMonths.push(found);
        this.availableMonths.sort((a, b) => a.value - b.value);
      }
    }
  }

  updateAvailableYears(selectedMonth: number): void {
    const today = new Date();
    const currentYear = today.getFullYear();

    if (selectedMonth === 1) {
      this.years = [currentYear - 1, currentYear];
    } else {
      this.years = [currentYear];
    }
    if (this.currentYear && !this.years.includes(this.currentYear)) {
      this.years.push(this.currentYear);
      this.years.sort();
    }
  }

  get currentModeName() {

    return this.selectedTransportModes[this.currentTransportIndex]?.name;
  }

  isWorkFromHomeSelected(): boolean {
    const currentMode = this.selectedTransportModes[this.currentTransportIndex];
    if (!currentMode) {
      return false;
    }
    const modeName = (currentMode.name || currentMode.transportmode_desc || '').toLowerCase();
    return Number(currentMode.id || currentMode.transportmode_id) === 16 || modeName.includes('work from home');
  }

  get maxDistance(): number {
    return this.unitType === 'Miles' ? 200 : 322;
  }

  get commutingDaysLabel(): string {
    return this.isWorkFromHomeSelected() ? 'No of WFH Days per Week' : 'No of Commuting Days per Week';
  }

  private configureDistanceControl(): void {
    const distanceControl = this.transportDetailForm?.get('distance');
    if (!distanceControl) {
      return;
    }

    const max = this.maxDistance;

    if (this.isWorkFromHomeSelected()) {
      distanceControl.setValue(0, { emitEvent: false });
      distanceControl.setValidators([Validators.required, Validators.min(0), Validators.max(max)]);
    } else {
      distanceControl.setValidators([Validators.required, Validators.min(0.1), Validators.max(max)]);
    }
    distanceControl.updateValueAndValidity({ emitEvent: false });
  }
}
